import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Database, ChevronDown, Search, Settings2, Scissors, Plus, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import type { PrefixSource } from '@/types/label';

interface LookupOption {
  value: string;
  record: Record<string, string>;
}

interface PrefixItem {
  search: string;
  fill: string;
}

interface DatasetInfo {
  id: string;
  name: string;
  fields: string[];
}

interface FieldWithLookupProps {
  field: string;
  value: string;
  onChange: (field: string, value: string) => void;
  onFillFromRecord?: (record: Record<string, string>) => void;
  lastChars?: number;
  onLastCharsChange?: (field: string, value: number) => void;
  prefixSources?: PrefixSource[];
  onPrefixSourcesChange?: (field: string, sources: PrefixSource[]) => void;
}

export const FieldWithLookup: React.FC<FieldWithLookupProps> = ({
  field,
  value,
  onChange,
  onFillFromRecord,
  lastChars: lastCharsProp = 0,
  onLastCharsChange,
  prefixSources: prefixSourcesProp = [],
  onPrefixSourcesChange,
}) => {
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [hasLookup, setHasLookup] = useState(false);
  const [open, setOpen] = useState(false);
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [prefixSearch, setPrefixSearch] = useState('');
  const [lastChars, setLastChars] = useState<number>(lastCharsProp);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [prefixSources, setPrefixSources] = useState<PrefixSource[]>(prefixSourcesProp);
  const [prefixItems, setPrefixItems] = useState<PrefixItem[]>([]);
  const [allDatasets, setAllDatasets] = useState<DatasetInfo[]>([]);

  useEffect(() => { setLastChars(lastCharsProp); }, [lastCharsProp]);
  useEffect(() => { setPrefixSources(prefixSourcesProp); }, [prefixSourcesProp]);

  // Load all datasets
  useEffect(() => {
    const load = async () => {
      const { data: datasets } = await supabase
        .from('data_datasets')
        .select('id, name, fields');
      if (datasets) {
        setAllDatasets(datasets.map(d => ({
          id: d.id,
          name: d.name,
          fields: d.fields as string[] || [],
        })));
      }
    };
    load();
  }, []);

  // Load lookup options (matching field name)
  useEffect(() => {
    const load = async () => {
      const matchingIds = allDatasets
        .filter(d => d.fields.includes(field))
        .map(d => d.id);

      if (matchingIds.length === 0) { setHasLookup(false); return; }
      setHasLookup(true);

      const { data: records } = await supabase
        .from('data_records')
        .select('field_data')
        .in('dataset_id', matchingIds);

      if (records) {
        const opts: LookupOption[] = records
          .map(r => {
            const fd = r.field_data as Record<string, string>;
            return { value: fd[field] || '', record: fd };
          })
          .filter(o => o.value);

        const seen = new Set<string>();
        const unique = opts.filter(o => {
          if (seen.has(o.value)) return false;
          seen.add(o.value);
          return true;
        });
        setOptions(unique);
      }
    };
    if (allDatasets.length > 0) load();
  }, [field, allDatasets]);

  // Load prefix items from configured sources
  useEffect(() => {
    const load = async () => {
      if (prefixSources.length === 0) { setPrefixItems([]); return; }

      const allItems: PrefixItem[] = [];
      for (const src of prefixSources) {
        const sField = src.searchField || src.field || '';
        const fField = src.fillField || src.field || '';
        if (!sField || !fField) continue;

        const { data: records } = await supabase
          .from('data_records')
          .select('field_data')
          .eq('dataset_id', src.datasetId);

        if (records) {
          records.forEach(r => {
            const fd = r.field_data as Record<string, string>;
            const searchVal = fd[sField] || '';
            const fillVal = fd[fField] || '';
            if (fillVal) {
              // Deduplicate by fill value
              if (!allItems.some(i => i.fill === fillVal && i.search === searchVal)) {
                allItems.push({ search: searchVal, fill: fillVal });
              }
            }
          });
        }
      }
      setPrefixItems(allItems);
    };
    load();
  }, [prefixSources]);

  const hasPrefixDropdown = prefixSources.length > 0 || hasLookup;

  // Build prefix display list
  const allPrefixItems = useMemo((): PrefixItem[] => {
    if (prefixSources.length > 0) return prefixItems;
    // Fallback: field-name-matched options
    if (hasLookup) return options.map(o => ({ search: o.value, fill: o.value }));
    return [];
  }, [prefixItems, prefixSources, hasLookup, options]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const s = search.toLowerCase();
    return options.filter(o => o.value.toLowerCase().includes(s));
  }, [options, search]);

  const prefixFiltered = useMemo(() => {
    if (!prefixSearch) return allPrefixItems;
    const s = prefixSearch.toLowerCase();
    return allPrefixItems.filter(v => v.search.toLowerCase().includes(s) || v.fill.toLowerCase().includes(s));
  }, [allPrefixItems, prefixSearch]);

  const applyLastChars = useCallback(() => {
    if (lastChars > 0 && value.length > lastChars) {
      onChange(field, value.slice(-lastChars));
    }
  }, [lastChars, value, field, onChange]);

  const handlePrefixSelect = useCallback((fillValue: string) => {
    onChange(field, fillValue + value);
    setPrefixOpen(false);
    setPrefixSearch('');
  }, [field, value, onChange]);

  const addPrefixSource = () => {
    if (allDatasets.length === 0) return;
    const ds = allDatasets[0];
    const f = ds.fields[0] || '';
    const newSources: PrefixSource[] = [...prefixSources, { datasetId: ds.id, field: f, searchField: f, fillField: f }];
    setPrefixSources(newSources);
    onPrefixSourcesChange?.(field, newSources);
  };

  const removePrefixSource = (index: number) => {
    const newSources = prefixSources.filter((_, i) => i !== index);
    setPrefixSources(newSources);
    onPrefixSourcesChange?.(field, newSources);
  };

  const updatePrefixSource = (index: number, key: keyof PrefixSource, val: string) => {
    const newSources = [...prefixSources];
    newSources[index] = { ...newSources[index], [key]: val };
    if (key === 'datasetId') {
      const ds = allDatasets.find(d => d.id === val);
      if (ds && ds.fields.length > 0) {
        newSources[index].searchField = ds.fields[0];
        newSources[index].fillField = ds.fields[0];
        newSources[index].field = ds.fields[0];
      }
    }
    setPrefixSources(newSources);
    onPrefixSourcesChange?.(field, newSources);
  };

  return (
    <div className="space-y-1">
      <Label className="text-[10px] sm:text-xs">{field}</Label>
      <div className="flex gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          placeholder={`Enter ${field}`}
          className="h-8 sm:h-9 text-xs sm:text-sm flex-1"
        />

        {lastChars > 0 && value.length > lastChars && (
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9 shrink-0"
            title={`Letzte ${lastChars} Zeichen nehmen`}
            onClick={applyLastChars}
          >
            <Scissors className="w-3.5 h-3.5" />
          </Button>
        )}

        {/* Prefix dropdown */}
        {hasPrefixDropdown && (
          <Popover open={prefixOpen} onOpenChange={setPrefixOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0"
                title="Präfix aus Datenbank voranstellen"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <div className="p-2 border-b border-border">
                <p className="text-[10px] text-muted-foreground mb-1 px-1">Wert voranstellen</p>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted">
                  <Search className="w-3 h-3 text-muted-foreground" />
                  <input
                    value={prefixSearch}
                    onChange={e => setPrefixSearch(e.target.value)}
                    placeholder="Suchen..."
                    className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <ScrollArea className="max-h-48">
                {prefixFiltered.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground text-center">Keine Treffer</p>
                ) : (
                  <div className="py-1">
                    {prefixFiltered.map((item, i) => (
                      <button
                        key={i}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
                        onClick={() => handlePrefixSelect(item.fill)}
                      >
                        <span className="font-medium">{item.search}</span>
                        {item.search !== item.fill && (
                          <span className="text-muted-foreground"> → {item.fill}</span>
                        )}
                        <span className="text-muted-foreground ml-1 opacity-60">= {item.fill}{value}</span>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {/* Full value lookup */}
        {hasLookup && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 shrink-0" title="Lookup aus Datenbank">
                <Database className="w-3.5 h-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              <div className="p-2 border-b border-border">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted">
                  <Search className="w-3 h-3 text-muted-foreground" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground" />
                </div>
              </div>
              <ScrollArea className="max-h-48">
                {filtered.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground text-center">Keine Treffer</p>
                ) : (
                  <div className="py-1">
                    {filtered.map((opt, i) => (
                      <button key={i} className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors" onClick={() => { onChange(field, opt.value); if (onFillFromRecord) onFillFromRecord(opt.record); setOpen(false); setSearch(''); }}>
                        {opt.value}
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {/* Settings */}
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 shrink-0" title="Feld-Einstellungen">
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Letzte X Zeichen</Label>
                <Input
                  type="number"
                  min={0}
                  value={lastChars || ''}
                  onChange={e => {
                    const val = parseInt(e.target.value) || 0;
                    setLastChars(val);
                    onLastCharsChange?.(field, val);
                  }}
                  placeholder="0 = deaktiviert"
                  className="h-8 text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  Nimmt nur die letzten X Zeichen des Werts. 0 = aus.
                </p>
              </div>

              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Präfix-Quellen</Label>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={addPrefixSource}>
                    <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Suchfeld = Anzeige im Dropdown. Füllfeld = Wert der vorangestellt wird.
                </p>
                {prefixSources.map((src, idx) => {
                  const ds = allDatasets.find(d => d.id === src.datasetId);
                  const fields = ds?.fields || [];
                  return (
                    <div key={idx} className="border border-border rounded-md p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-muted-foreground">Quelle {idx + 1}</span>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removePrefixSource(idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <Select value={src.datasetId} onValueChange={v => updatePrefixSource(idx, 'datasetId', v)}>
                        <SelectTrigger className="h-7 text-[10px]">
                          <SelectValue placeholder="Dataset..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allDatasets.map(d => (
                            <SelectItem key={d.id} value={d.id} className="text-xs">{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-1">
                        <div>
                          <span className="text-[9px] text-muted-foreground">Suchfeld</span>
                          <Select value={src.searchField || src.field || ''} onValueChange={v => updatePrefixSource(idx, 'searchField', v)}>
                            <SelectTrigger className="h-7 text-[10px]">
                              <SelectValue placeholder="Suchfeld..." />
                            </SelectTrigger>
                            <SelectContent>
                              {fields.map(f => (
                                <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <span className="text-[9px] text-muted-foreground">Füllfeld</span>
                          <Select value={src.fillField || src.field || ''} onValueChange={v => updatePrefixSource(idx, 'fillField', v)}>
                            <SelectTrigger className="h-7 text-[10px]">
                              <SelectValue placeholder="Füllfeld..." />
                            </SelectTrigger>
                            <SelectContent>
                              {fields.map(f => (
                                <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {prefixSources.length === 0 && hasLookup && (
                  <p className="text-[10px] text-muted-foreground italic">
                    Automatisch: Feld "{field}" aus passenden Datasets.
                  </p>
                )}
                {prefixSources.length === 0 && !hasLookup && (
                  <p className="text-[10px] text-muted-foreground italic">
                    Keine Quellen konfiguriert. Klicke "Hinzufügen".
                  </p>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};